import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_KEY;
const tenantId = import.meta.env.VITE_TENANT_ID;

if (!supabaseUrl || !supabaseKey) {
  throw new Error('Missing Supabase environment variables');
}

if (!tenantId) {
  console.warn('VITE_TENANT_ID is not defined. Using default tenant_id=1');
}

// Créer le client Supabase avec des headers personnalisés pour le multi-tenant
export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // Utiliser 'implicit' en développement pour éviter les problèmes de PKCE
    // En production, PKCE est recommandé pour plus de sécurité
    flowType: import.meta.env.DEV ? 'implicit' : 'pkce',
    // Augmenter la durée avant expiration du token
    storage: window.localStorage,
    storageKey: 'supabase.auth.token'
  },
  db: {
    schema: 'public'
  },
  global: {
    headers: {
      'x-tenant-id': tenantId || '1'
    }
  }
});

// Écouter les changements d'état d'authentification pour gérer les sessions expirées
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'TOKEN_REFRESHED') {
    console.log('Token rafraîchi automatiquement');
  } else if (event === 'SIGNED_OUT') {
    console.log('Utilisateur déconnecté');
  } else if (event === 'USER_UPDATED') {
    console.log('Utilisateur mis à jour');
  }
});

// ✅ INTERCEPTEURS ACTIVÉS avec résolution par domaine
// Ces intercepteurs ajoutent automatiquement tenant_id et domain à TOUTES les requêtes INSERT/UPDATE/UPSERT
// 
// NOUVELLE LOGIQUE (résolution par domaine) :
// - Récupère tenant_id depuis le cache localStorage (résolu par domaine via TenantContext)
// - Ajoute automatiquement domain (hostname actuel)
// - Fallback sur VITE_TENANT_ID uniquement si aucun cache disponible
//
// Avantages :
// - Les pages n'ont plus besoin de gérer explicitement tenant_id/domain
// - Utilise la résolution par domaine (ex: ecatheba.com → tenant_id: 3)
// - Cohérent avec le système multi-tenant

/**
 * Récupère le tenant_id résolu depuis le cache (TenantContext)
 * Fallback sur VITE_TENANT_ID uniquement si pas de cache
 */
const getResolvedTenantId = (): number => {
  // Essayer de récupérer le tenant_id résolu depuis le cache sessionStorage
  if (typeof window !== 'undefined') {
    try {
      // Le cache tenant utilise sessionStorage avec la clé 'sis_tenant_cache'
      const cachedTenant = sessionStorage.getItem('sis_tenant_cache');
      if (cachedTenant) {
        const parsed = JSON.parse(cachedTenant);
        if (parsed.id) {
          return parseInt(parsed.id);
        }
      }
    } catch (error) {
      console.warn('Erreur lors de la récupération du tenant depuis le cache:', error);
    }
  }
  
  // Fallback sur VITE_TENANT_ID
  return parseInt(tenantId || '2');
};

/**
 * Récupère le domaine actuel
 */
const getCurrentDomain = (): string | null => {
  if (typeof window !== 'undefined') {
    return window.location.hostname;
  }
  return null;
};

// Intercepter toutes les requêtes pour ajouter automatiquement tenant_id et domain
const { insert, update, upsert } = supabase.from('').constructor.prototype;

// Surcharge de la méthode insert
supabase.from('').constructor.prototype.insert = function(values: any, options?: any) {
  const resolvedTenantId = getResolvedTenantId();
  const domain = getCurrentDomain();
  
  // Si c'est un tableau d'objets
  if (Array.isArray(values)) {
    values = values.map(row => {
      // Ne pas écraser si tenant_id est déjà défini explicitement
      const newRow = { ...row };
      if (newRow.tenant_id === undefined) {
        newRow.tenant_id = resolvedTenantId;
      }
      if (newRow.domain === undefined && domain) {
        newRow.domain = domain;
      }
      return newRow;
    });
  } 
  // Si c'est un objet unique
  else if (values && typeof values === 'object') {
    values = { ...values };
    if (values.tenant_id === undefined) {
      values.tenant_id = resolvedTenantId;
    }
    if (values.domain === undefined && domain) {
      values.domain = domain;
    }
  }
  
  return insert.call(this, values, options);
};

// Surcharge de la méthode update
supabase.from('').constructor.prototype.update = function(values: any, options?: any) {
  const resolvedTenantId = getResolvedTenantId();
  const domain = getCurrentDomain();
  
  if (values && typeof values === 'object') {
    values = { ...values };
    if (values.tenant_id === undefined) {
      values.tenant_id = resolvedTenantId;
    }
    if (values.domain === undefined && domain) {
      values.domain = domain;
    }
  }
  
  return update.call(this, values, options);
};

// Surcharge de la méthode upsert
supabase.from('').constructor.prototype.upsert = function(values: any, options?: any) {
  const resolvedTenantId = getResolvedTenantId();
  const domain = getCurrentDomain();
  
  // Si c'est un tableau d'objets
  if (Array.isArray(values)) {
    values = values.map(row => {
      const newRow = { ...row };
      if (newRow.tenant_id === undefined) {
        newRow.tenant_id = resolvedTenantId;
      }
      if (newRow.domain === undefined && domain) {
        newRow.domain = domain;
      }
      return newRow;
    });
  } 
  // Si c'est un objet unique
  else if (values && typeof values === 'object') {
    values = { ...values };
    if (values.tenant_id === undefined) {
      values.tenant_id = resolvedTenantId;
    }
    if (values.domain === undefined && domain) {
      values.domain = domain;
    }
  }
  
  return upsert.call(this, values, options);
};

export const signIn = async (email: string, password: string) => {
  try {
    // Vérifier d'abord si l'utilisateur est actif avant de l'authentifier
    const { data: userByEmail, error: userByEmailError } = await supabase
      .from('users')
      .select('is_active')
      .eq('email', email)
      .single();
    
    // Si l'utilisateur existe et est inactif, bloquer immédiatement
    if (userByEmail && userByEmail.is_active === false) {
      console.log('Tentative de connexion avec un compte inactif:', email);
      throw new Error('Votre compte est inactif. Veuillez contacter l\'administrateur.');
    }

    // Procéder à l'authentification seulement si le compte est actif ou n'existe pas encore
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      console.error('Sign in error:', error);
      throw error;
    }
    
    // Double vérification après authentification (au cas où)
    if (data.user) {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('is_active')
        .eq('id', data.user.id)
        .single();
      
      if (userError) {
        console.error('User data fetch error:', userError);
        // Déconnecter l'utilisateur si on ne peut pas vérifier son statut
        await supabase.auth.signOut();
        throw new Error('Impossible de vérifier le statut du compte');
      }
      
      if (userData && userData.is_active === false) {
        // Déconnecter l'utilisateur s'il est inactif
        await supabase.auth.signOut();
        throw new Error('Votre compte est inactif. Veuillez contacter l\'administrateur.');
      }
    }

    return { data, error: null };
  } catch (error: any) {
    console.error('Sign in catch error:', error);
    return {
      data: null,
      error: {
        message: error.message || 'Une erreur est survenue lors de la connexion'
      }
    };
  }
};

export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Sign out error:', error);
      throw error;
    }
    return { error: null };
  } catch (error: any) {
    console.error('Sign out catch error:', error);
    return {
      error: {
        message: error.message || 'Une erreur est survenue lors de la déconnexion'
      }
    };
  }
};

export const getSession = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) {
      console.error('Get session error:', error);
      throw error;
    }
    return { session, error: null };
  } catch (error: any) {
    console.error('Get session catch error:', error);
    return {
      session: null,
      error: {
        message: error.message || 'Une erreur est survenue lors de la récupération de la session'
      }
    };
  }
};
